import { IconList } from '../../ui/heroicons-picker/iconType';

export interface RoutingCondition {
  id: string;
  letter: string;
  field: string;
  operator: 'equals' | 'contains' | 'less_than' | 'greater_than' | 'in';
  value: string | string[];
}

/** Terminal action configuration for leaf track nodes */
export interface TrackTerminalAction {
  type: 'link' | 'calendar' | 'navigate' | 'escalation' | 'start_onboarding';
  value: string;
  calendar_config?: {
    id: string;
    name: string;
    details: Record<string, any>;
  };
  fallback_calendar_config?: {
    id: string;
    name: string;
    details: Record<string, any>;
  };
}

/** Classification rules for deterministic track entry */
export interface TrackClassificationRules {
  conditions: RoutingCondition[];
  formula: string;
}

export interface VisitorTrackNode {
  /** Stable client identifier for React keys, etc. */
  id: string;
  /** Human-friendly label shown in the UI. */
  name: string;
  /** Auto-generated system ID (unique across nodes), e.g. track_1 */
  system_name: string;
  /** Parent node system_name; null only for the root node. */
  parent_system_name: string | null;
  /** Per-parent ordering among siblings. */
  sort_order: number;

  /** AI/fuzzy classification instructions for entering this track. */
  classification_instructions: string;
  /** Logical rules for deterministic classification (evaluated before AI instructions). */
  classification_rules?: TrackClassificationRules;

  /** What to accomplish/collect while in this track. */
  conversation_goals: string;
  /** Array of form field/question `name` values to collect at this track level. */
  fields_to_collect: string[];
  /** Array of AI label `name` values to require at this track level. */
  labels_to_collect?: string[];

  /** Terminal action for leaf nodes only (link, calendar, or escalation). */
  terminal_action?: TrackTerminalAction;

  /**
   * Optional free-form prompt/instructions for what the AI should do when this
   * track is a terminal leaf (in addition to any configured terminal_action).
   */
  terminal_action_prompt?: string;

  /**
   * When true, this node was auto-created as the child of a node with a
   * `start_onboarding` terminal action.  It is locked (cannot be deleted,
   * renamed, or moved independently) and serves as the root of the
   * onboarding sub-tree.
   */
  is_onboarding_node?: boolean;
}

/** Activation config for a skill: always-on or gated by routing conditions. */
export interface SkillActivation {
  mode: 'always' | 'conditional';
  conditions: RoutingCondition[];
  formula: string;
}

export type AgentSkillSurface = 'website' | 'app';

export interface AgentSkill {
  /** Stable identifier (random id assigned at creation). */
  id: string;
  /**
   * Legacy field from the pre-launch "system skills" design; every skill is
   * customer-authored now. Kept optional so old draft configs still parse —
   * the Skills tab migrates 'system' entries on load.
   */
  type?: 'system' | 'custom';
  name: string;
  /**
   * One-line summary shown to the AI only while a conditional skill is inactive.
   * Should describe what becomes possible, not what to do.
   */
  description: string;
  /** Surfaces where the skill can become available. */
  surfaces: AgentSkillSurface[];
  enabled: boolean;
  activation: SkillActivation;
  /** Prompt injected while the skill is active. */
  instructions: string;
  /** Tool names granted while the skill is active. */
  tools: string[];
  /**
   * Calendar snapshot for skills granting show_booking_calendar — same
   * {id, name, details} shape as track terminal_action.calendar_config.
   * Required for calendar-granting skills to become active.
   */
  calendar_config?: {
    id: string;
    name: string;
    details: Record<string, any>;
  };
  /**
   * Escalation routing for skills granting escalate_to_human. Absent =
   * legacy behavior (notify everyone opted in, first come first serve).
   */
  escalation_config?: SkillEscalationConfig;
}

/** One entry in an escalation skill's ordered rep pool. */
export interface SkillEscalationRep {
  id: string;
  email: string;
  name: string;
}

export interface SkillEscalationConfig {
  /** 'none' = free-for-all (default); 'round_robin' rotates through reps in order. */
  assignment: 'none' | 'round_robin';
  /**
   * What a matched CRM account owner means for this pool:
   * 'assignee' = they take the chat (rotation covers unmatched),
   * 'notify' = rotation assigns, owner gets an FYI mention (default),
   * 'context' = shown by name only, no ping.
   */
  account_owner_role: 'assignee' | 'notify' | 'context';
  /** Ordered rep pool; empty = legacy org-wide heuristic. */
  reps: SkillEscalationRep[];
}

/**
 * Conditional skills with no rules never activate at runtime (SkillService).
 * Used to warn in the UI and block save before that silent failure.
 */
export function isConditionalSkillMissingRules(skill: AgentSkill): boolean {
  if (skill.activation?.mode !== 'conditional') return false;
  const conditions = skill.activation?.conditions || [];
  return !conditions.some((condition) => Boolean(condition?.letter));
}

export interface TabProps {
  agId?: string;
  agentName: string;
  setNeedsUpdate: (value: boolean) => void;
}

export interface InstructionsTabProps extends TabProps {
  agentName: string;
  setAgentName: (value: string) => void;
  /** Form fields + skills feed the @-mention catalog for goal/persona. */
  form: any[];
  skills: AgentSkill[];
  goal: string;
  setGoal: (value: string) => void;
  persona: string;
  setPersona: (value: string) => void;
}

export interface KnowledgeTabProps extends TabProps {
  website: string;
  setWebsite: (value: string) => void;
  selectedPages: Set<string>;
  setSelectedPages: (value: Set<string>) => void;
  indexedPages: any[];
  setIndexedPages: (value: any[]) => void;
}

export interface CustomizationTabProps extends TabProps {
  color: string;
  setColor: (value: string) => void;
  endColor: string;
  setEndColor: (value: string) => void;
  colorScheme: string;
  setColorScheme: (value: string) => void;
  font: string;
  setFont: (value: string) => void;
  welcomeMessage: string;
  setWelcomeMessage: (value: string) => void;
  links: any[];
  setLinks: (value: any[]) => void;
  starters: any[];
  setStarters: (value: any[]) => void;
  autoOpenEnabled: boolean;
  setAutoOpenEnabled: (value: boolean) => void;
  autoOpenSeconds: number;
  setAutoOpenSeconds: (value: number) => void;
  openOncePerSession: boolean;
  setOpenOncePerSession: (value: boolean) => void;
  popupMessageEnabled: boolean;
  setPopupMessageEnabled: (value: boolean) => void;
  popupMessage: string;
  setPopupMessage: (value: string) => void;
  contextualPopupEnabled: boolean;
  setContextualPopupEnabled: (value: boolean) => void;
  contextualStartersEnabled: boolean;
  setContextualStartersEnabled: (value: boolean) => void;
  enableFollowUps: boolean;
  setEnableFollowUps: (value: boolean) => void;
  followUpInstructions: string;
  setFollowUpInstructions: (value: string) => void;
  file: File | null;
  setFile: (file: File | null) => void;
  previewUrl: string;
  setPreviewUrl: (url: string) => void;
  iconFile: File | null;
  setIconFile: (file: File | null) => void;
  iconPreviewUrl: string;
  setIconPreviewUrl: (url: string) => void;
  agentIconType: string;
  setAgentIconType: (type: string) => void;
  agentIcon: IconList;
  setAgentIcon: (icon: IconList) => void;
  setFileChanged: (changed: boolean) => void;
  setIconFileChanged: (changed: boolean) => void;
  escalationEnabled: boolean;
  setEscalationEnabled: (value: boolean) => void;
  escalationButtons: any[];
  setEscalationButtons: (value: any[]) => void;
  gdprEnabled: boolean;
  setGdprEnabled: (value: boolean) => void;
  searchBarEnabled: boolean;
  setSearchBarEnabled: (value: boolean) => void;
  searchBarPlaceholder: string;
  setSearchBarPlaceholder: (value: string) => void;
  hideAimdoc: boolean;
  setHideAimdoc: (value: boolean) => void;
  enableVisitorLocation: boolean;
  setEnableVisitorLocation: (value: boolean) => void;
  enablePageAwareness: boolean;
  setEnablePageAwareness: (value: boolean) => void;
  smartNavigationEnabled: boolean;
  setSmartNavigationEnabled: (value: boolean) => void;
  rateLimitMessages: number;
  setRateLimitMessages: (value: number) => void;
  rateLimitSeconds: number;
  setRateLimitSeconds: (value: number) => void;
  rateLimitMessage: string;
  setRateLimitMessage: (value: string) => void;
  agentGatewayEnabled: boolean;
  setAgentGatewayEnabled: (value: boolean) => void;
  agentGatewayDailyCap: number;
  setAgentGatewayDailyCap: (value: number) => void;
  visibilityRules: any[];
  setVisibilityRules: (value: any[]) => void;
  contextualExclusionRules: any[];
  setContextualExclusionRules: (value: any[]) => void;
  showGloballyAfterEngagement: boolean;
  setShowGloballyAfterEngagement: (value: boolean) => void;
  widgetPosition: string;
  setWidgetPosition: (value: string) => void;
  disclaimerMessage?: string;
  setDisclaimerMessage?: (value: string) => void;
  privacyPolicyLink?: string;
  setPrivacyPolicyLink?: (value: string) => void;
  darkModeEnabled: boolean;
  setDarkModeEnabled: (value: boolean) => void;
  darkModeDefault: 'system' | 'light' | 'dark';
  setDarkModeDefault: (value: 'system' | 'light' | 'dark') => void;
  darkModeColor: string;
  setDarkModeColor: (value: string) => void;
}

export interface AgentGatewayTabProps extends TabProps {
  agentGatewayEnabled: boolean;
  setAgentGatewayEnabled: (value: boolean) => void;
  agentGatewayDailyCap: number;
  setAgentGatewayDailyCap: (value: number) => void;
  agentGatewaySites: string[];
  setAgentGatewaySites: (value: string[]) => void;
}

// Email channel settings are non-versioned (saved via their own PUT, outside
// draft/publish), so the tab needs no state setters from ConfigureAgent.
export interface ProactiveTabProps extends TabProps {
  proactiveEnabled: boolean;
  setProactiveEnabled: (value: boolean) => void;
  proactiveMinInactivityHours: string;
  setProactiveMinInactivityHours: (value: string) => void;
  proactiveMaxUnanswered: string;
  setProactiveMaxUnanswered: (value: string) => void;
  proactiveEmailFallback: boolean;
  setProactiveEmailFallback: (value: boolean) => void;
  proactiveInstructions: string;
  setProactiveInstructions: (value: string) => void;
  // Website (anonymous) visitors: triggered by the warm-visitor tracker.
  proactiveVisitorsEnabled: boolean;
  setProactiveVisitorsEnabled: (value: boolean) => void;
  proactiveVisitorsMinInactivityHours: string;
  setProactiveVisitorsMinInactivityHours: (value: string) => void;
  proactiveVisitorsMaxUnanswered: string;
  setProactiveVisitorsMaxUnanswered: (value: string) => void;
  proactiveVisitorsDailyCap: string;
  setProactiveVisitorsDailyCap: (value: string) => void;
  proactiveVisitorsInstructions: string;
  setProactiveVisitorsInstructions: (value: string) => void;
  /** Form fields + skills feed the @-mention catalog (InstructionsTab idiom). */
  form: any[];
  skills: AgentSkill[];
}

export interface EmailChannelTabProps {
  agId?: string;
  agentName: string;
}

export interface DataCaptureTabProps extends TabProps {
  form: any[];
  setForm: (value: any[]) => void;
  outcomes: OutcomeDefinition[];
  setOutcomes: (value: OutcomeDefinition[]) => void;
  enableTracks: boolean;
  /** Current workflow track nodes (used for validating field deletes). */
  trackNodes: VisitorTrackNode[];
  /** Setter for workflow track nodes (used for auto-cleaning field references on delete). */
  setTrackNodes: (value: VisitorTrackNode[]) => void;
  /** Current agent tools/actions config (CRM, scheduling, etc). */
  actions: Record<string, any>;
  /** Setter for actions/tools config (used for auto-cleaning field mappings on delete). */
  setActions: (value: Record<string, any>) => void;
  qualifyVisitor: boolean;
  setQualifyVisitor: (value: boolean) => void;
  workEmailOnly: boolean;
  setWorkEmailOnly: (value: boolean) => void;
  requireContactInfo: boolean;
  setRequireContactInfo: (value: boolean) => void;
  messagesBeforeContactInfo: number;
  setMessagesBeforeContactInfo: (value: number) => void;
  hideContactUsButton: boolean;
  setHideContactUsButton: (value: boolean) => void;
}

export interface OutcomeDefinition {
  name: string;
  label: string;
  description: string;
  active?: boolean;
  sort_order?: number;
}

export interface WorkflowTabProps extends TabProps {
  form: any[];
  goal: string;
  persona: string;
  trackNodes: VisitorTrackNode[];
  setTrackNodes: (value: VisitorTrackNode[]) => void;
  enableTracks: boolean;
  setEnableTracks: (value: boolean) => void;
  enableSkills: boolean;
  actions: Record<string, any>;
}

export interface SkillsTabProps extends TabProps {
  form: any[];
  skills: AgentSkill[];
  setSkills: (value: AgentSkill[]) => void;
  enableSkills: boolean;
  setEnableSkills: (value: boolean) => void;
  enableTracks: boolean;
  /**
   * Agent tools/actions config (legacy "tools" key, really integration
   * config) — used for the one-time escalation prompt import and the
   * calendar-connected hint.
   */
  actions: Record<string, any>;
}

export interface AssessmentsTabProps extends TabProps {
  actions: Record<string, any>;
  setActions: (value: Record<string, any>) => void;
}

export interface McpServersTabProps extends TabProps {
  actions: Record<string, any>;
  setActions: (value: Record<string, any>) => void;
}

export interface ActionsTabProps extends TabProps {
  actions: Record<string, any>;
  setActions: (value: Record<string, any>) => void;
  form: any[];
  escalationEnabled: boolean;
  enableTracks: boolean;
}

export interface ActionsProps {
  actions: Record<string, any>;
  setActions: (value: Record<string, any>) => void;
  formFields: any[];
  agentId?: string;
  agentName: string;
  setNeedsUpdate: (value: boolean) => void;
}

export interface VoiceOption {
  id: string;
  name: string;
  description?: string;
  preview_url?: string;
}

export interface VoiceTabProps extends TabProps {
  voiceEnabled: boolean;
  setVoiceEnabled: (value: boolean) => void;
  selectedVoice: VoiceOption | null;
  setSelectedVoice: (value: VoiceOption | null) => void;
}

export interface AppModeTabProps extends TabProps {
  onboardingUrl: string;
  setOnboardingUrl: (value: string) => void;
  onboardingAllowlistUrls: string[];
  setOnboardingAllowlistUrls: (value: string[]) => void;
  onlyShowForAdmin: boolean;
  setOnlyShowForAdmin: (value: boolean) => void;
}
